/**
 * `<caos-picklist-field>` — single-select over a STATIC set of values.
 * (Catalog: Fields & input › PicklistField — a field choosing one value from a fixed list.)
 *
 * Options are supplied as data, never queried: set the `options` property to an
 * array of `{ value, label, qualifier? }` (or plain strings), or declare `<option>`
 * children in light DOM. States mirror the other fields: label, placeholder (a
 * disabled leading choice), value, disabled, invalid + validation message, required,
 * and the accent focus ring. Every colour/space/radius reads a `--caos-*` token.
 *
 * CAOS-569 — TWO THINGS A FIXED LIST STILL NEEDS, both declared as inputs rather than
 * coded into a screen:
 *
 *   A QUALIFIER LINE. An option may carry `qualifier`, a quieter second line under its
 *   label — what the option IS, for a set long enough that the label alone will not
 *   tell two apart. A native `<option>` cannot draw a second line, so a qualifier only
 *   ever shows in the `filterable` render (below); it is otherwise carried but unshown.
 *
 *   A DEPENDENCY ON ANOTHER FIELD. `dependsOn` names the field this one waits on, purely
 *   declaratively; `dependsOnValue` is that field's CURRENT value, written by the host
 *   every time it changes — the same "data in, not queried" shape `value`/`options`
 *   already use. While `dependsOn` is set and `dependsOnValue` is empty, the control
 *   renders EMPTY of real options — placeholder only — but stays enabled and focusable:
 *   waiting is not disabled and not hidden. The gate is purely a RENDER-time read of
 *   these two attributes; it does not touch what `options` holds, so whichever of the
 *   two ways below re-supplies options keeps working once the dependency is satisfied:
 *     • the host sets `.options` itself in response to the dependency changing (already
 *       possible before this ticket; now gets the waiting affordance for free), or
 *     • the host also sets `optionsSource`, an injected `(dependsOnValue) => options |
 *       Promise<options>` seam — the same shape as `CaosLookupField.search` — which this
 *       piece calls itself whenever `dependsOnValue` changes to a new, non-empty value.
 *   One component serves both the "list B depends on list A's choice" case and the
 *   plain fixed-list case: the difference is entirely which inputs a placement sets.
 *
 * A FILTERABLE RENDER. Setting `filterable` draws a typeahead over a listbox instead of
 * the native `<select>` — needed once an option carries a `qualifier` (there is nowhere
 * to put a second line in a native option) or once the set is long enough that typing
 * beats scanning. Typing filters by substring against BOTH the label and the qualifier,
 * case-insensitively. The underlying native `<select>` still exists, hidden, so `name`
 * still forwards a value into a submitted form exactly as it always has.
 *
 * CAOS-597 — SEVERAL VALUES FROM THE SAME LIST. Setting `multiple` draws the same options as a
 * group of checkboxes, one per value, named by the field's label, and what the field holds becomes
 * the list `values` instead of the single `value`. It is a presentation of this field rather than a
 * field of its own, because what is chosen from is still one declared list — the value set a
 * MultiSelect field draws from. Once somebody has used a `required` group and left nothing chosen, it
 * shows as invalid on its own, with `message` or "Choose at least one." A value in `values` that is
 * not among the options is kept rather than dropped, so a value since retired from the list is not
 * erased by the next save; it is simply not drawn. In this presentation `filterable`, `placeholder`
 * and `value` do nothing. Its seam is `::part(group)`.
 *
 * CAOS-640 — THE SAME CHOICE DRAWN AS CARDS. Setting `presentation="cards"` draws the options as a
 * row of selectable cards instead of a list, so a choice of plan, tier, finish or configuration can
 * look the way that choice normally looks. A card shows whichever of `image`, `icon`, `label`,
 * `description` and `amount` its option actually carries and draws nothing for the rest: an option
 * carrying none of them is a card with a label on it, and a whole set carrying none of them is the
 * plain list it always was. WHICH presentation is used is declared by the value set the options come
 * from, never decided here by inspecting them. `multiple` still wins — several values is the
 * checkbox group, and two presentations cannot both be the visible one. The cards are a radiogroup
 * of buttons (one choice among several permanently visible options), the group is one tab stop, and
 * arrow keys move within it. `amount` is drawn as given; formatting a figure is the caller's to do.
 *
 * Styling seams: `::part(label)`, `::part(select)`, `::part(message)`, — filterable only —
 * `::part(combo)`, `::part(input)`, `::part(listbox)`, `::part(option)`, and — cards only —
 * `::part(cards)`, `::part(card)`. Fires a bubbling, composed `change` in every mode, on every
 * commit of a new value.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { deepActiveElement } from '../focus.js';
import { isIconName, makeIcon } from './icons.js';
import './checkbox-field.js';

export interface CaosPicklistOption {
  value: string;
  label: string;
  /** A quieter second line under the label — what the option IS, when the label alone will not tell two apart. */
  qualifier?: string;
  /**
   * CAOS-640 — what an option carries when it is drawn as a CARD rather than as a line of text.
   * Every one is optional, and an option carrying none of them draws exactly as it always has.
   */
  /** A picture for this option — any address the surrounding document can load. */
  image?: string;
  /** A glyph from the shared icon vocabulary. One this library cannot draw is skipped, not drawn blank. */
  icon?: string;
  /**
   * A line of explanation under the label. Distinct from `qualifier`, which is CAOS-569's
   * dropdown-density device: a qualifier says what the option IS so a long list stays scannable,
   * a description says what choosing it MEANS. Each render draws ONE second line and prefers the
   * one it exists for — the dropdown draws a qualifier and falls back to a description, a card
   * draws a description and falls back to a qualifier — so the two never compete for the same space.
   */
  description?: string;
  /** The figure qualifying this option (a price difference, a duration, a count). Drawn as given. */
  amount?: number;
}

/**
 * CAOS-640 — how a set of options is presented. `dropdown` is everything this field did before and
 * stays the default; `cards` draws the same options as a row of selectable cards. It is a
 * PRESENTATION of one declared list, not a different field — the same reasoning `multiple` records.
 */
export type CaosPicklistPresentation = 'dropdown' | 'cards' | 'radio';

/**
 * The injected result source for a DEPENDENT dropdown (CAOS-569) — the same "data in, not
 * queried" shape as `CaosLookupField`'s `search`. Called with the depended-on field's
 * current value; returns (or resolves to) the options that value implies.
 */
export type CaosPicklistOptionsSource = (
  dependsOnValue: string,
) => CaosPicklistOption[] | Promise<CaosPicklistOption[]>;

const sheet = makeSheet(`
  :host { display: block; }

  .field { display: flex; flex-direction: column; gap: var(--caos-space-1); }

  .label {
    font-family: var(--caos-font);
    font-size: var(--caos-text-body);
    font-weight: 600;
    color: var(--caos-color-text-muted);
  }
  .label:empty { display: none; }
  .req { color: var(--caos-color-danger); margin-left: 2px; }

  .wrap { position: relative; display: block; }

  select {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    width: 100%;
    min-width: 0;
    min-height: 40px;
    padding: var(--caos-space-3) calc(var(--caos-space-5) + var(--caos-space-2)) var(--caos-space-3) 14px;
    border-radius: var(--caos-radius);
    border: 1px solid var(--caos-color-border);
    background: var(--caos-color-surface-2);
    color: var(--caos-color-text);
    font-family: var(--caos-font);
    font-size: var(--caos-text-body);
    line-height: 1.4;
    cursor: pointer;
    transition: border-color 0.12s ease, box-shadow 0.12s ease, background 0.12s ease;
  }
  /* placeholder selection (empty value) reads muted */
  select:invalid, select.is-placeholder { color: var(--caos-color-text-muted); }

  select:hover:not(:disabled):not(:focus) { border-color: color-mix(in srgb, var(--caos-color-accent) 45%, var(--caos-color-border)); }
  /* Focus = accent wash + soft strong border, not a solid ring (Dragon §08). */
  select:focus-visible, select:focus {
    outline: none;
    border-color: var(--caos-color-border-strong);
    box-shadow: inset 0 0 0 100px var(--caos-accent-wash);
  }
  select:disabled {
    background: var(--caos-color-surface-2);
    color: var(--caos-color-text-muted);
    cursor: not-allowed;
    opacity: var(--caos-state-disabled-opacity);
  }

  .chevron {
    position: absolute;
    top: 50%;
    right: var(--caos-space-3);
    transform: translateY(-50%);
    width: 16px;
    height: 16px;
    pointer-events: none;
    color: var(--caos-color-text-muted);
  }
  .chevron svg { display: block; width: 100%; height: 100%; }

  :host([invalid]) select { border-color: var(--caos-color-danger); }
  :host([invalid]) select:focus-visible,
  :host([invalid]) select:focus {
    border-color: var(--caos-color-danger);
    box-shadow: inset 0 0 0 100px color-mix(in srgb, var(--caos-color-danger) 6%, transparent);
  }

  .message {
    font-family: var(--caos-font);
    font-size: 11.5px;
    line-height: 1.4;
    color: var(--caos-color-text-muted);
  }
  .message:empty { display: none; }
  :host([invalid]) .message { color: var(--caos-color-danger); }

  /* ── Inline (record-detail) variant — the P5b in-place editor: no box, an accent
     underline, inherits the surrounding font. The boxed select above is the form default. */
  :host([inline]) { display: inline-block; width: 100%; }
  :host([inline]) .field { gap: 0; }
  :host([inline]) .label, :host([inline]) .message { display: none; }
  :host([inline]) select {
    min-height: 0;
    padding: 0 20px 2px 0;
    border: none;
    border-bottom: 1px solid var(--caos-color-accent);
    border-radius: 0;
    background: transparent;
    box-shadow: none;
    font-family: inherit;
    font-size: inherit;
    color: inherit;
  }
  :host([inline]) .chevron { right: 2px; }
  :host([inline]) select:hover:not(:disabled):not(:focus) { border-bottom-color: var(--caos-color-accent); }
  :host([inline]) select:focus-visible, :host([inline]) select:focus {
    border-bottom-color: var(--caos-color-accent);
    background: transparent;
    box-shadow: none;
  }

  /* ── CAOS-569 filterable render — a typeahead over a listbox, replacing the native
     select visually (the select stays in the DOM, hidden, so its form name still submits). ── */
  .wrap[hidden] { display: none; }
  .combo { position: relative; display: none; }
  :host([filterable]) .combo { display: block; }

  .combo .icon {
    position: absolute;
    top: 50%;
    left: var(--caos-space-3);
    transform: translateY(-50%);
    width: 16px;
    height: 16px;
    color: var(--caos-color-text-muted);
    pointer-events: none;
  }
  .combo .icon svg { display: block; width: 100%; height: 100%; }

  .combo input {
    width: 100%;
    min-width: 0;
    min-height: 40px;
    padding: var(--caos-space-3) calc(var(--caos-space-5) + var(--caos-space-2)) var(--caos-space-3) calc(var(--caos-space-5) + var(--caos-space-1));
    border-radius: var(--caos-radius);
    border: 1px solid var(--caos-color-border);
    background: var(--caos-color-surface-2);
    color: var(--caos-color-text);
    font-family: var(--caos-font);
    font-size: var(--caos-text-body);
    line-height: 1.4;
    transition: border-color 0.12s ease, box-shadow 0.12s ease, background 0.12s ease;
  }
  .combo input::placeholder { color: var(--caos-color-text-muted); opacity: 0.85; }
  .combo input:hover:not(:disabled):not(:focus) { border-color: color-mix(in srgb, var(--caos-color-accent) 45%, var(--caos-color-border)); }
  .combo input:focus-visible, .combo input:focus {
    outline: none;
    border-color: var(--caos-color-border-strong);
    box-shadow: inset 0 0 0 100px var(--caos-accent-wash);
  }
  .combo input:disabled { background: var(--caos-color-surface-2); color: var(--caos-color-text-muted); cursor: not-allowed; opacity: var(--caos-state-disabled-opacity); }
  :host([invalid]) .combo input { border-color: var(--caos-color-danger); }
  :host([invalid]) .combo input:focus-visible, :host([invalid]) .combo input:focus {
    border-color: var(--caos-color-danger);
    box-shadow: inset 0 0 0 100px color-mix(in srgb, var(--caos-color-danger) 6%, transparent);
  }

  .listbox {
    position: absolute;
    /* A NAMED layer, not a number — see the header note on layering (CAOS-586). */
    z-index: var(--caos-layer-dropdown);
    top: calc(100% + var(--caos-space-1));
    left: 0;
    right: 0;
    max-height: 260px;
    overflow-y: auto;
    margin: 0;
    padding: var(--caos-space-1);
    list-style: none;
    background: var(--caos-color-surface);
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius);
    box-shadow: var(--caos-shadow-float);
  }
  .listbox[hidden] { display: none; }

  .option {
    display: flex;
    flex-direction: column;
    gap: 1px;
    padding: var(--caos-space-2) var(--caos-space-3);
    border-radius: var(--caos-radius-sm);
    cursor: pointer;
  }
  .option[aria-selected="true"] { background: var(--caos-state-selected-bg); }
  .option.active { background: var(--caos-color-surface-2); }
  .option[aria-selected="true"].active { background: color-mix(in srgb, var(--caos-color-accent) 20%, var(--caos-color-surface)); }
  .option .opt-label { font-size: var(--caos-text-row); line-height: 1.35; color: var(--caos-color-text); }
  .option .opt-sub { font-size: 11.5px; line-height: 1.3; color: var(--caos-color-text-muted); }

  .status {
    padding: var(--caos-space-2) var(--caos-space-3);
    font-size: 12.5px;
    color: var(--caos-color-text-muted);
  }

  :host([inline]) .combo input {
    min-height: 0;
    padding: 0 var(--caos-space-5) 2px calc(var(--caos-space-4) + 2px);
    border: none;
    border-bottom: 1px solid var(--caos-color-accent);
    border-radius: 0;
    background: transparent;
    box-shadow: none;
    font-family: inherit;
    font-size: inherit;
    color: inherit;
  }
  :host([inline]) .combo .icon { left: 0; }
  :host([inline]) .combo input:hover:not(:disabled):not(:focus) { border-bottom-color: var(--caos-color-accent); }
  :host([inline]) .combo input:focus-visible, :host([inline]) .combo input:focus {
    border-bottom-color: var(--caos-color-accent);
    background: transparent;
    box-shadow: none;
  }

  /* ── CAOS-597 multiple — the same options as a group of checkboxes ── */
  .group { display: none; flex-direction: column; gap: var(--caos-space-1); }
  :host([multiple]) .group { display: flex; }
  :host([multiple]) .wrap,
  :host([multiple]) .combo { display: none; }
  .message.is-refusal { color: var(--caos-color-danger); }

  /* ── CAOS-640 cards — the same one-of-a-set choice, drawn as selectable cards. The multiple
     render still wins (below), so the checkbox group never competes with this for the same space.
     NOTE: no backticks in here — this comment sits inside a template literal. ── */
  .cards { display: none; flex-wrap: wrap; gap: var(--caos-space-2); }
  :host([presentation="cards"]) .cards { display: flex; }
  :host([presentation="cards"]) .wrap,
  :host([presentation="cards"]) .combo { display: none; }
  :host([multiple]) .cards { display: none; }

  /* ── CAOS-600 radio — the same one-of-a-set choice, drawn as plain radio buttons. It is the
     cards render with the cards taken away: permanently expanded options, one choosable at a
     time, over the one declared list. Native radios rather than drawn ones, so arrow-key
     movement, the single tab stop and the checked semantics are the platform's rather than
     this file's. The native select still exists, hidden, so the form name still submits.
     NOTE: no backticks in here — this comment sits inside a template literal. ── */
  .radios { display: none; flex-direction: column; gap: var(--caos-space-1); }
  :host([presentation="radio"]) .radios { display: flex; }
  :host([presentation="radio"]) .wrap,
  :host([presentation="radio"]) .combo { display: none; }
  :host([multiple]) .radios { display: none; }

  .radio {
    display: flex;
    align-items: baseline;
    gap: var(--caos-space-2);
    font-family: var(--caos-font);
    font-size: var(--caos-text-body);
    color: var(--caos-color-text);
    cursor: pointer;
  }
  .radio input {
    /* The control keeps its own box; only the accent is ours, so the platform's focus ring,
       checked mark and hit area all survive. */
    accent-color: var(--caos-color-accent);
    cursor: pointer;
  }
  .radio input:disabled,
  .radio input:disabled + .radio-text { cursor: not-allowed; color: var(--caos-color-text-muted); }
  /* An option's second line reads the same here as it does on a card. */
  .radio-text { display: flex; flex-direction: column; gap: 2px; }
  .radio-desc { font-size: 11.5px; color: var(--caos-color-text-muted); }

  .card {
    display: flex;
    flex-direction: column;
    gap: var(--caos-space-1);
    /* A plain basis rather than a token of its own: CAOS-652 pins the single exempt
       component-scoped token, and a page wanting a different card width already has ::part(card). */
    flex: 1 1 150px;
    max-width: 100%;
    min-width: 0;
    padding: var(--caos-space-3);
    border-radius: var(--caos-radius);
    border: 1px solid var(--caos-color-border);
    background: var(--caos-color-surface-2);
    color: var(--caos-color-text);
    font-family: var(--caos-font);
    text-align: left;
    cursor: pointer;
    transition: border-color 0.12s ease, box-shadow 0.12s ease, background 0.12s ease;
  }
  .card:hover:not(:disabled) { border-color: color-mix(in srgb, var(--caos-color-accent) 45%, var(--caos-color-border)); }
  .card:focus-visible {
    outline: none;
    border-color: var(--caos-color-border-strong);
    box-shadow: inset 0 0 0 100px var(--caos-accent-wash);
  }
  /* Chosen is carried by the BORDER and a wash, never by colour alone. */
  .card[aria-checked="true"] {
    border-color: var(--caos-color-accent);
    background: var(--caos-state-selected-bg);
  }
  .card:disabled { cursor: not-allowed; opacity: var(--caos-state-disabled-opacity); color: var(--caos-color-text-muted); }

  .card .card-img {
    display: block;
    width: 100%;
    aspect-ratio: 3 / 2;
    object-fit: cover;
    border-radius: var(--caos-radius-sm);
    background: var(--caos-color-surface);
  }
  .card .card-icon { display: block; width: var(--caos-size-xs-height); height: var(--caos-size-xs-height); color: var(--caos-color-text-muted); }
  .card .card-icon svg { display: block; width: 100%; height: 100%; }
  .card .card-label { font-size: var(--caos-text-row); line-height: 1.35; font-weight: 600; }
  .card .card-desc { font-size: 11.5px; line-height: 1.3; color: var(--caos-color-text-muted); }
  .card .card-amount { font-size: var(--caos-text-row); line-height: 1.3; color: var(--caos-color-text); font-variant-numeric: tabular-nums; }

  :host([invalid]) .card { border-color: var(--caos-color-danger); }
`);
/** An id for the select so its own label can point at it (CAOS-587 review). */
let picklistUid = 0;

export class CaosPicklistField extends CaosElement {
  static get observedAttributes(): string[] {
    return [
      'label',
      'placeholder',
      'value',
      'disabled',
      'required',
      'invalid',
      'message',
      'name',
      'filterable',
      'depends-on',
      'depends-on-value',
      'multiple',
      'presentation',
    ];
  }

  private select: HTMLSelectElement;
  private labelEl: HTMLLabelElement;
  private reqEl: HTMLSpanElement;
  private messageEl: HTMLDivElement;
  private selectWrap: HTMLDivElement;
  private group: HTMLDivElement;

  // ── cards render (CAOS-640) ──
  private cards: HTMLDivElement;
  private cardEls: HTMLButtonElement[] = [];

  // ── filterable render (CAOS-569) ──
  private combo: HTMLDivElement;
  private input: HTMLInputElement;
  private listbox: HTMLUListElement;
  private optionEls: HTMLLIElement[] = [];
  private activeIndex = -1;
  private comboOpen = false;

  // ── several values (CAOS-597) ──
  private _values: string[] = [];
  /** Somebody used the group and left nothing chosen — a required group says so on its own. */
  private emptyAfterUse = false;

  /** The radio render's container — see the constructor. */
  private radios!: HTMLDivElement;
  private _options: CaosPicklistOption[] | null = null;
  private _optionsSource: CaosPicklistOptionsSource | null = null;
  private dependencyToken = 0;
  private readonly seq = ++picklistUid;
  private readonly listboxId = `caos-picklist-list-${this.seq}`;

  private onDocPointer = (e: Event): void => {
    if (!e.composedPath().includes(this)) this.closeCombo();
  };

  constructor() {
    super(sheet);

    const field = document.createElement('div');
    field.className = 'field';

    this.labelEl = document.createElement('label');
    this.labelEl.className = 'label';
    this.labelEl.setAttribute('part', 'label');
    this.labelEl.id = `caos-picklist-label-${this.seq}`;
    this.reqEl = document.createElement('span');
    this.reqEl.className = 'req';
    this.reqEl.textContent = '*';
    this.reqEl.hidden = true;
    this.reqEl.setAttribute('aria-hidden', 'true');
    this.labelEl.append(this.reqEl);

    // ── native select (the form default, and the always-present form-submission path) ──
    this.selectWrap = document.createElement('div');
    this.selectWrap.className = 'wrap';

    this.select = document.createElement('select');
    this.select.id = `caos-picklist-${this.seq}`;
    this.labelEl.htmlFor = this.select.id;
    this.select.setAttribute('part', 'select');
    this.select.addEventListener('change', () => {
      this.commitValue(this.select.value);
    });

    const chevron = document.createElement('span');
    chevron.className = 'chevron';
    chevron.setAttribute('aria-hidden', 'true');
    // CAOS-602 — the vocabulary's own `chevron` (down), which this had re-drawn point for point.
    chevron.append(makeIcon('chevron'));

    this.selectWrap.append(this.select, chevron);

    // ── filterable combobox (CAOS-569) — hidden unless the `filterable` input is set ──
    this.combo = document.createElement('div');
    this.combo.className = 'combo';
    this.combo.setAttribute('part', 'combo');

    const icon = document.createElement('span');
    icon.className = 'icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.append(makeIcon('search'));

    this.input = document.createElement('input');
    this.input.id = `caos-picklist-input-${this.seq}`;
    this.input.type = 'text';
    this.input.setAttribute('part', 'input');
    this.input.setAttribute('role', 'combobox');
    this.input.setAttribute('aria-autocomplete', 'list');
    this.input.setAttribute('aria-expanded', 'false');
    this.input.setAttribute('aria-controls', this.listboxId);
    this.input.autocomplete = 'off';
    this.input.addEventListener('input', () => this.onComboInput());
    this.input.addEventListener('keydown', (e) => this.onComboKeydown(e));
    this.input.addEventListener('focus', () => this.openCombo());

    this.listbox = document.createElement('ul');
    this.listbox.className = 'listbox';
    this.listbox.id = this.listboxId;
    this.listbox.setAttribute('part', 'listbox');
    this.listbox.setAttribute('role', 'listbox');
    this.listbox.hidden = true;

    this.combo.append(icon, this.input, this.listbox);

    this.messageEl = document.createElement('div');
    this.messageEl.className = 'message';
    this.messageEl.setAttribute('part', 'message');

    // ── several values (CAOS-597) — drawn only when `multiple` is set ──
    this.group = document.createElement('div');
    this.group.className = 'group';
    this.group.setAttribute('part', 'group');
    this.group.setAttribute('role', 'group');
    this.group.setAttribute('aria-labelledby', this.labelEl.id);
    this.messageEl.id = `caos-picklist-message-${this.seq}`;
    this.group.setAttribute('aria-describedby', this.messageEl.id);

    // ── cards (CAOS-640) — drawn only when `presentation="cards"`. A radiogroup rather than a
    // listbox: this is one choice out of several visible, permanently expanded options, which is
    // what a radio group IS. The native select stays in the DOM, hidden, so `name` still submits.
    this.cards = document.createElement('div');
    this.cards.className = 'cards';
    this.cards.setAttribute('part', 'cards');
    this.cards.setAttribute('role', 'radiogroup');
    this.cards.setAttribute('aria-labelledby', this.labelEl.id);
    this.cards.setAttribute('aria-describedby', this.messageEl.id);

    // ── radios (CAOS-600) — drawn only when `presentation="radio"`. No `role` is set on it: a
    // group of native radio inputs sharing a name IS a radio group to the platform, and declaring
    // the role a second time here would describe the group twice.
    this.radios = document.createElement('div');
    this.radios.className = 'radios';
    this.radios.setAttribute('part', 'radios');
    this.radios.setAttribute('aria-labelledby', this.labelEl.id);
    this.radios.setAttribute('aria-describedby', this.messageEl.id);

    field.append(this.labelEl, this.selectWrap, this.combo, this.cards, this.radios, this.group, this.messageEl);
    this.root.append(field);

    this.addEventListener('focusout', () => {
      setTimeout(() => {
        const active = deepActiveElement();
        if (!active || (!this.contains(active) && !this.root.contains(active))) this.closeCombo();
      }, 0);
    });
  }

  connectedCallback(): void {
    if (this._options === null) this.readLightDomOptions();
    this.refresh();
  }

  disconnectedCallback(): void {
    document.removeEventListener('pointerdown', this.onDocPointer, true);
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (name === 'depends-on-value' && newValue !== oldValue) {
      // CAOS-569 — a NEW, non-empty dependency value re-queries through the injected seam, if
      // one is set. Becoming empty just re-engages the waiting gate at render time (`isWaiting`)
      // — nothing to fetch for "no dependency chosen yet".
      if (newValue && this._optionsSource) void this.fetchDependentOptions(newValue);
    }
    this.refresh();
  }

  /** The selectable values (data in, not queried). */
  get options(): CaosPicklistOption[] {
    return this._options ?? [];
  }
  set options(list: Array<CaosPicklistOption | string>) {
    this._options = list.map((o) => (typeof o === 'string' ? { value: o, label: o } : o));
    this.refresh();
  }

  /** The injected result source for a dependent dropdown (CAOS-569). Data in, not queried. */
  get optionsSource(): CaosPicklistOptionsSource | null {
    return this._optionsSource;
  }
  set optionsSource(fn: CaosPicklistOptionsSource | null) {
    this._optionsSource = fn;
    const dep = this.dependsOnValue;
    if (fn && dep) void this.fetchDependentOptions(dep);
  }

  get value(): string {
    return this.getAttribute('value') ?? '';
  }
  set value(v: string) {
    this.setAttribute('value', v);
  }

  /**
   * CAOS-597 — what is chosen when `multiple` is set, in the order the options list them. Setting it
   * replaces the whole choice. A value that is not among the options is kept, after the rest, and not
   * drawn.
   */
  get values(): string[] {
    const known = this.effectiveOptions().map((o) => o.value);
    const chosen = new Set(this._values);
    return [...known.filter((v) => chosen.has(v)), ...this._values.filter((v) => !known.includes(v))];
  }
  set values(list: readonly string[]) {
    const seen = new Set<string>();
    this._values = (Array.isArray(list) ? list : []).filter((v): v is string => {
      if (typeof v !== 'string' || seen.has(v)) return false;
      seen.add(v);
      return true;
    });
    this.emptyAfterUse = false;
    this.refresh();
  }

  /** Choose several values, drawn as a group of checkboxes (CAOS-597). */
  get multiple(): boolean {
    return this.hasAttribute('multiple');
  }
  set multiple(v: boolean) {
    this.toggleAttribute('multiple', v);
  }

  get filterable(): boolean {
    return this.hasAttribute('filterable');
  }
  set filterable(v: boolean) {
    if (v) this.setAttribute('filterable', '');
    else this.removeAttribute('filterable');
  }

  /**
   * CAOS-640 — draw the same options as a row of selectable cards. CAOS-600 — or as plain radio
   * buttons. Anything else reads as `dropdown`, so a presentation nobody here can draw degrades to
   * the list rather than to an empty field.
   */
  get presentation(): CaosPicklistPresentation {
    const declared = this.getAttribute('presentation');
    return declared === 'cards' || declared === 'radio' ? declared : 'dropdown';
  }
  set presentation(p: CaosPicklistPresentation) {
    if (p === 'cards' || p === 'radio') this.setAttribute('presentation', p);
    else this.removeAttribute('presentation');
  }

  /**
   * Whether the cards render is the one actually showing. `multiple` WINS: its own contract says a
   * group of checkboxes is what several-values looks like, and two presentations cannot both be the
   * visible one. So asking for cards and several values gets the checkbox group, not a hybrid.
   */
  private get cardsMode(): boolean {
    return this.presentation === 'cards' && !this.multiple;
  }

  /**
   * Whether the radio render is the one actually showing. `multiple` wins for the same reason it
   * wins over cards: one radio is choosable at a time, which is not what several values look like.
   */
  private get radioMode(): boolean {
    return this.presentation === 'radio' && !this.multiple;
  }

  /** The field this one depends on — declarative only; see the file header. */
  get dependsOn(): string {
    return this.getAttribute('depends-on') ?? '';
  }
  set dependsOn(v: string) {
    if (v) this.setAttribute('depends-on', v);
    else this.removeAttribute('depends-on');
  }

  /** The CURRENT value of the field named by `dependsOn` — written by the host on every change. */
  get dependsOnValue(): string {
    return this.getAttribute('depends-on-value') ?? '';
  }
  set dependsOnValue(v: string) {
    if (v) this.setAttribute('depends-on-value', v);
    else this.removeAttribute('depends-on-value');
  }

  /** Focus the active control (the inline record-detail editor auto-focuses on click-to-edit). */
  override focus(options?: FocusOptions): void {
    if (this.multiple) {
      this.group.querySelector<HTMLElement>('caos-checkbox-field')?.focus(options);
      return;
    }
    (this.filterable ? this.input : this.select).focus(options);
  }

  /** Read declarative `<option>` children (light DOM) as the option source. */
  private readLightDomOptions(): void {
    const declared = Array.from(this.querySelectorAll('option'));
    if (declared.length) {
      this._options = declared.map((o) => ({
        value: o.value || o.textContent || '',
        label: o.textContent || o.value || '',
        qualifier: o.dataset.qualifier || undefined,
      }));
    }
  }

  /**
   * CAOS-569 — a dependency that is declared but not yet satisfied renders as though there were
   * no options at all: placeholder only, control still enabled. This is a pure RENDER gate — it
   * never touches `_options` itself, so whichever way the host re-supplies them (setting
   * `.options` directly, or via `optionsSource`) starts showing the moment the gate opens.
   */
  private isWaiting(): boolean {
    return this.hasAttribute('depends-on') && this.dependsOnValue === '';
  }

  private effectiveOptions(): CaosPicklistOption[] {
    return this.isWaiting() ? [] : (this._options ?? []);
  }

  private async fetchDependentOptions(dependsOnValue: string): Promise<void> {
    const source = this._optionsSource;
    if (!source) return;
    const token = ++this.dependencyToken;
    try {
      const result = await source(dependsOnValue);
      if (token !== this.dependencyToken) return; // superseded by a newer dependency value
      this._options = result ?? [];
    } catch {
      if (token !== this.dependencyToken) return;
      this._options = [];
    }
    this.refresh();
  }

  private commitValue(value: string): void {
    this.setAttribute('value', value);
    this.refresh();
    this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
  }

  private refresh(): void {
    this.renderSelectOptions();
    this.renderGroup();
    this.renderCards();
    this.renderRadios();
    this.sync();
  }

  /** CAOS-597 — one checkbox per option, drawn only when `multiple` is set. */
  private renderGroup(): void {
    if (!this.multiple) {
      if (this.group.childElementCount) this.group.replaceChildren();
      return;
    }
    const chosen = new Set(this._values);
    const name = this.getAttribute('name');
    this.group.replaceChildren(
      ...this.effectiveOptions().map((o) => {
        const box = document.createElement('caos-checkbox-field');
        box.dataset.value = o.value;
        if (chosen.has(o.value)) box.setAttribute('checked', '');
        if (name !== null) box.setAttribute('name', name);
        box.append(document.createTextNode(o.label));
        box.addEventListener('change', (e) => {
          // The box's own change would cross this shadow root as a second one; the field reports once.
          e.stopPropagation();
          this.chooseValue(o.value, box.checked);
        });
        return box;
      }),
    );
  }

  /**
   * CAOS-640 — one card per option, drawn only in the cards presentation. A card shows whichever of
   * image, icon, label, description and amount its option carries and NOTHING for the rest, so an
   * option carrying none of them is a card with a label on it rather than a frame full of holes.
   */
  private renderCards(): void {
    if (!this.cardsMode) {
      if (this.cards.childElementCount) this.cards.replaceChildren();
      this.cardEls = [];
      return;
    }
    const disabled = this.hasAttribute('disabled');
    const chosen = this.value;
    const options = this.effectiveOptions();
    // The group is ONE tab stop, as a radio group is: whatever is chosen is the stop, and with
    // nothing chosen yet the first card is, so the cards can always be reached from the keyboard.
    const found = options.findIndex((o) => o.value === chosen);
    const stop = found >= 0 ? found : 0;
    this.cardEls = options.map((o, i) => {
      const card = document.createElement('button');
      card.type = 'button';
      card.className = 'card';
      card.setAttribute('part', 'card');
      card.setAttribute('role', 'radio');
      card.setAttribute('aria-checked', String(o.value === chosen));
      card.dataset['value'] = o.value;
      card.disabled = disabled;
      card.tabIndex = i === stop ? 0 : -1;
      if (o.image) {
        const img = document.createElement('img');
        img.className = 'card-img';
        img.src = o.image;
        // The label sits in the same card, so the picture adds nothing for a screen reader to read.
        img.alt = '';
        card.append(img);
      } else if (o.icon && isIconName(o.icon)) {
        // A name this vocabulary does not draw is SKIPPED — never an empty frame where a glyph
        // should be. `isIconName` is the library's own answer to that question.
        const glyph = document.createElement('span');
        glyph.className = 'card-icon';
        glyph.setAttribute('aria-hidden', 'true');
        glyph.append(makeIcon(o.icon));
        card.append(glyph);
      }
      const label = document.createElement('span');
      label.className = 'card-label';
      label.textContent = o.label;
      card.append(label);
      // A card leads with what choosing this MEANS; a qualifier stands in when there is no
      // description, so an option written for the dropdown still reads properly here.
      const sub = o.description ?? o.qualifier;
      if (sub) {
        const desc = document.createElement('span');
        desc.className = 'card-desc';
        desc.textContent = sub;
        card.append(desc);
      }
      // `0` is a real figure ("adds nothing"), so presence is what decides whether it is drawn.
      if (o.amount !== undefined) {
        const amount = document.createElement('span');
        amount.className = 'card-amount';
        amount.textContent = String(o.amount);
        card.append(amount);
      }
      card.addEventListener('click', () => this.commitValue(o.value));
      card.addEventListener('keydown', (e) => this.onCardKeydown(e, i));
      return card;
    });
    this.cards.replaceChildren(...this.cardEls);
  }

  /**
   * CAOS-600 — one native radio per option, drawn only in the radio presentation.
   *
   * The inputs share a generated name of their own rather than the field's `name`. The hidden
   * native select already carries that, and two controls submitting the same name would send the
   * value twice — which is exactly the kind of thing a presentation is not allowed to change about
   * the field it presents.
   */
  private renderRadios(): void {
    if (!this.radioMode) {
      if (this.radios.childElementCount) this.radios.replaceChildren();
      return;
    }
    const disabled = this.hasAttribute('disabled');
    const chosen = this.value;
    const group = `caos-radio-${this.seq}`;
    this.radios.replaceChildren(
      ...this.effectiveOptions().map((o) => {
        const row = document.createElement('label');
        row.className = 'radio';
        row.setAttribute('part', 'radio');
        const box = document.createElement('input');
        box.type = 'radio';
        box.name = group;
        box.value = o.value;
        box.checked = o.value === chosen;
        box.disabled = disabled;
        box.addEventListener('change', () => {
          if (box.checked) this.commitValue(o.value);
        });
        const text = document.createElement('span');
        text.className = 'radio-text';
        const label = document.createElement('span');
        label.textContent = o.label;
        text.append(label);
        // The same second line a card draws, so an option written for one presentation reads
        // properly in the other; a qualifier stands in when there is no description.
        const sub = o.description ?? o.qualifier;
        if (sub) {
          const desc = document.createElement('span');
          desc.className = 'radio-desc';
          desc.textContent = sub;
          text.append(desc);
        }
        row.append(box, text);
        return row;
      }),
    );
  }

  /**
   * Arrow keys move within the cards and choose as they go, which is what a radio group does — and
   * why the cards are one tab stop rather than one per option.
   */
  private onCardKeydown(e: KeyboardEvent, index: number): void {
    const count = this.cardEls.length;
    if (count === 0) return;
    let next: number;
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = (index + 1) % count;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = (index - 1 + count) % count;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = count - 1;
        break;
      default:
        return;
    }
    e.preventDefault();
    const option = this.effectiveOptions()[next];
    if (!option) return;
    this.commitValue(option.value);
    // Committing re-draws the cards, so focus whichever card now holds that position.
    this.cardEls[next]?.focus();
  }

  private chooseValue(value: string, on: boolean): void {
    const rest = this._values.filter((v) => v !== value);
    this._values = on ? [...rest, value] : rest;
    this.emptyAfterUse = this.values.length === 0;
    this.sync();
    this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
  }

  private renderSelectOptions(): void {
    this.select.replaceChildren();
    const placeholder = this.getAttribute('placeholder');
    if (placeholder !== null) {
      const opt = document.createElement('option');
      opt.value = '';
      opt.textContent = placeholder;
      opt.disabled = true;
      opt.hidden = true;
      this.select.append(opt);
    }
    for (const o of this.effectiveOptions()) {
      const opt = document.createElement('option');
      opt.value = o.value;
      opt.textContent = o.label;
      this.select.append(opt);
    }
  }

  private reflectPlaceholderClass(): void {
    this.select.classList.toggle('is-placeholder', this.select.value === '');
  }

  private optionMatches(o: CaosPicklistOption, query: string): boolean {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    // CAOS-640 — an option with no `qualifier` but a `description` is matched on the line it
    // actually shows, since that is what the combo falls back to drawing.
    return o.label.toLowerCase().includes(q) || (o.qualifier ?? o.description ?? '').toLowerCase().includes(q);
  }

  private selectedOption(): CaosPicklistOption | undefined {
    const value = this.value;
    return this.effectiveOptions().find((o) => o.value === value);
  }

  private sync(): void {
    const labelText = this.getAttribute('label') ?? '';
    this.labelEl.childNodes.forEach((n) => {
      if (n.nodeType === Node.TEXT_NODE) n.remove();
    });
    if (labelText) this.labelEl.insertBefore(document.createTextNode(labelText), this.reqEl);
    this.reqEl.hidden = !this.hasAttribute('required');

    const value = this.getAttribute('value');
    if (value !== null) this.select.value = value;
    else if (this.getAttribute('placeholder') !== null) this.select.value = '';

    const disabled = this.hasAttribute('disabled');
    this.select.disabled = disabled;
    this.select.required = this.hasAttribute('required');
    if (this.hasAttribute('invalid')) this.select.setAttribute('aria-invalid', 'true');
    else this.select.removeAttribute('aria-invalid');

    const name = this.getAttribute('name');
    if (name !== null) this.select.name = name;
    if (labelText) this.select.setAttribute('aria-label', labelText);

    this.reflectPlaceholderClass();
    const emptyAndRequired = this.multiple && this.emptyAfterUse && this.hasAttribute('required');
    this.messageEl.textContent = this.getAttribute('message') ?? (emptyAndRequired ? 'Choose at least one.' : '');
    this.messageEl.classList.toggle('is-refusal', emptyAndRequired);
    const groupInvalid = this.hasAttribute('invalid') || emptyAndRequired;
    if (groupInvalid) this.group.setAttribute('aria-invalid', 'true');
    else this.group.removeAttribute('aria-invalid');
    this.group.querySelectorAll('caos-checkbox-field').forEach((box) => {
      box.toggleAttribute('invalid', groupInvalid);
      box.toggleAttribute('disabled', disabled);
    });

    // ── which render mode is visible ──
    // CAOS-640 — there are now three presentations over one declared list, and they are mutually
    // exclusive. `multiple` wins over all of them (see `cardsMode`); cards then displaces the
    // filterable combo, which in turn displaces the native select. The select is only ever HIDDEN,
    // never removed, so `name` keeps submitting a value in every one of them.
    const cardsMode = this.cardsMode;
    const radioMode = this.radioMode;
    const filterable = this.filterable && !cardsMode && !radioMode;
    this.selectWrap.hidden = filterable || cardsMode || radioMode;

    // ── the filterable input mirrors the select's state ──
    this.input.disabled = disabled;
    this.input.placeholder = this.getAttribute('placeholder') ?? '';
    if (this.hasAttribute('invalid')) this.input.setAttribute('aria-invalid', 'true');
    else this.input.removeAttribute('aria-invalid');
    if (labelText) {
      this.input.setAttribute('aria-label', labelText);
      if (!this.labelEl.htmlFor && filterable) this.labelEl.htmlFor = this.input.id;
    }
    this.labelEl.htmlFor = filterable ? this.input.id : this.select.id;
    if (document.activeElement !== this.input) {
      const selected = this.selectedOption();
      this.input.value = selected?.label ?? '';
    }
    if (!filterable) this.closeCombo();
  }

  // ── filterable combobox (CAOS-569) ──

  private onComboInput(): void {
    this.renderComboList();
  }

  private openCombo(): void {
    if (this.isWaiting() || this.hasAttribute('disabled')) return;
    if (this.comboOpen) return;
    this.comboOpen = true;
    this.listbox.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
    document.addEventListener('pointerdown', this.onDocPointer, true);
    this.renderComboList();
  }

  private closeCombo(): void {
    if (!this.comboOpen) return;
    this.comboOpen = false;
    this.listbox.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.activeIndex = -1;
    document.removeEventListener('pointerdown', this.onDocPointer, true);
    // typed text that was never committed reverts to the real selection
    const selected = this.selectedOption();
    this.input.value = selected?.label ?? '';
  }

  private renderComboList(): void {
    this.listbox.replaceChildren();
    this.optionEls = [];
    this.activeIndex = -1;
    const filtered = this.effectiveOptions().filter((o) => this.optionMatches(o, this.input.value));
    if (filtered.length === 0) {
      // While waiting on a dependency there is nothing to say beyond the placeholder already
      // showing in the field itself — an extra "no matches" line here would repeat that, not add
      // to it, so it is left blank rather than noisy. Once ready, an empty result IS worth a line.
      if (!this.isWaiting()) {
        const li = document.createElement('li');
        li.className = 'status';
        li.setAttribute('role', 'presentation');
        li.textContent = 'No matches.';
        this.listbox.append(li);
      }
      this.input.removeAttribute('aria-activedescendant');
      return;
    }
    filtered.forEach((opt, i) => {
      const li = document.createElement('li');
      li.className = 'option';
      li.setAttribute('part', 'option');
      li.setAttribute('role', 'option');
      li.id = `${this.listboxId}-opt-${i}`;
      li.setAttribute('aria-selected', String(opt.value === this.value));
      const label = document.createElement('span');
      label.className = 'opt-label';
      label.textContent = opt.label;
      li.append(label);
      if (opt.qualifier) {
        const sub = document.createElement('span');
        sub.className = 'opt-sub';
        sub.textContent = opt.qualifier;
        li.append(sub);
      }
      li.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        this.selectComboIndex(i, filtered);
      });
      li.addEventListener('pointerenter', () => this.setActive(i));
      this.listbox.append(li);
      this.optionEls.push(li);
    });
    this.input.removeAttribute('aria-activedescendant');
  }

  private setActive(index: number): void {
    this.activeIndex = index;
    this.optionEls.forEach((el, i) => el.classList.toggle('active', i === index));
    const active = this.optionEls[index];
    if (active) {
      this.input.setAttribute('aria-activedescendant', active.id);
      active.scrollIntoView({ block: 'nearest' });
    } else {
      this.input.removeAttribute('aria-activedescendant');
    }
  }

  private selectComboIndex(index: number, filtered: CaosPicklistOption[]): void {
    const opt = filtered[index];
    if (!opt) return;
    this.commitValue(opt.value);
    this.input.value = opt.label;
    this.closeCombo();
  }

  private onComboKeydown(e: KeyboardEvent): void {
    const filtered = () => this.effectiveOptions().filter((o) => this.optionMatches(o, this.input.value));
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!this.comboOpen) {
          this.openCombo();
        } else if (this.optionEls.length) {
          this.setActive(this.activeIndex >= this.optionEls.length - 1 ? 0 : this.activeIndex + 1);
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (this.comboOpen && this.optionEls.length) {
          this.setActive(this.activeIndex <= 0 ? this.optionEls.length - 1 : this.activeIndex - 1);
        }
        break;
      case 'Home':
        if (this.comboOpen && this.optionEls.length) {
          e.preventDefault();
          this.setActive(0);
        }
        break;
      case 'End':
        if (this.comboOpen && this.optionEls.length) {
          e.preventDefault();
          this.setActive(this.optionEls.length - 1);
        }
        break;
      case 'Enter':
        if (this.comboOpen && this.activeIndex >= 0) {
          e.preventDefault();
          this.selectComboIndex(this.activeIndex, filtered());
        }
        break;
      case 'Escape':
        if (this.comboOpen) {
          e.preventDefault();
          this.closeCombo();
        }
        break;
    }
  }
}

define('caos-picklist-field', CaosPicklistField);

declare global {
  interface HTMLElementTagNameMap {
    'caos-picklist-field': CaosPicklistField;
  }
}
